import type { ReactNode } from "react";
import { Delta, MetricCard, cn, type MetricDelta } from "@fox1ck-org/ui";
import { DimensionFilterOp } from "@gen/tracker/v1/reporting_pb";

import { NIL_UUID } from "@/components/reports/names";
import { formatInt, formatPct } from "@/lib/format";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { useT } from "@/lib/i18n";
import { useLanderLibrary } from "@/lib/services/lander-service";
import { useReport } from "@/lib/services/report-service";
import { useReportingSchema } from "@/lib/services/reporting-schema-service";

import { bagOf, countDelta, rateDelta } from "./numbers";

export interface Kpi {
  key: string;
  label: string;
  value: string;
  delta?: MetricDelta;
  note?: ReactNode;
  tone?: "ok" | "warn" | "danger";
  noteTone?: "warn" | "danger";
}

/** A row of MetricCards in one frame, as the boards draw the numbers over a list. */
export function KpiStrip({ tiles, loading, className }: { tiles: Kpi[]; loading?: boolean; className?: string }) {
  return (
    <div className={cn("grid shrink-0 auto-cols-fr grid-flow-col overflow-hidden rounded-lg border border-border bg-card", className)}>
      {tiles.map((k) => (
        <MetricCard
          labelCase="none"
          key={k.key}
          label={k.label}
          value={k.value}
          loading={loading}
          tone={k.tone}
          comparison={<Comparison delta={k.delta} note={k.note} tone={k.noteTone} />}
          className="min-w-0 rounded-none border-0 border-l border-border bg-transparent px-3.5 pb-3 pt-2.5 first:border-l-0"
        />
      ))}
    </div>
  );
}

function Comparison({ delta, note, tone }: { delta?: MetricDelta; note?: ReactNode; tone?: "warn" | "danger" }) {
  if (!delta && !note) return <span aria-hidden>&nbsp;</span>;
  return (
    <span className="flex min-w-0 items-baseline gap-1.5 overflow-hidden whitespace-nowrap">
      {delta ? <Delta {...delta} compact icon={false} /> : null}
      {note ? <span className={cn("min-w-0 truncate", tone === "warn" && "text-warn", tone === "danger" && "text-destructive")}>{note}</span> : null}
    </span>
  );
}

/**
 * The library's six numbers (board LanderList): how many landers and how many
 * a live campaign serves, then the period's traffic of every lander — the
 * report engine's «Итого» by «Ленд» (archive included, a page without a
 * lander left out), never a sum of the page on screen — and the risk flags.
 */
export function LibraryKpis() {
  const t = useT();
  const p = useReportParams();
  const library = useLanderLibrary();
  const schema = useReportingSchema();
  const report = useReport({
    streamIds: [],
    from: p.from,
    to: p.to,
    timezone: p.tz,
    dims: ["landing"],
    basis: p.basis,
    filters: { dims: [{ key: "landing", op: DimensionFilterOp.NOT_IN, values: [NIL_UUID] }], metrics: [] },
    subtotals: false,
    compare: p.compareRange,
    limit: 1,
  });
  const v = bagOf(report.data?.totals, schema.data) ?? {};
  const prev = bagOf(report.data?.compareTotals, schema.data);
  const lib = library.data;
  const risky = lib ? Number(lib.risky) : undefined;
  const danger = lib ? Number(lib.dangerInCampaigns) : 0;
  const tiles: Kpi[] = [
    { key: "landers", label: t("landers.kpi.landers"), value: formatInt(lib ? Number(lib.landers) : undefined), note: lib ? t("landers.kpi.inCampaigns", { n: formatInt(Number(lib.inCampaigns)) }) : undefined },
    { key: "visits", label: t("landers.kpi.visits"), value: formatInt(v.visits), delta: countDelta(v.visits, prev?.visits), note: t("landers.kpi.people", { n: formatInt(v.clicks) }) },
    { key: "went", label: t("landers.kpi.wentOn"), value: formatInt(v.cta_clicks), delta: countDelta(v.cta_clicks, prev?.cta_clicks), note: t("landers.kpi.wentOn.note") },
    { key: "ctr", label: t("landers.kpi.lpCtr"), value: formatPct(v.lp_ctr), delta: rateDelta(v.lp_ctr, prev?.lp_ctr) },
    { key: "leads", label: t("landers.kpi.leads"), value: formatInt(v.leads), delta: countDelta(v.leads, prev?.leads), note: t("landers.kpi.cr", { cr: formatPct(v.cr) }) },
    {
      key: "risk",
      label: t("landers.kpi.risky"),
      value: formatInt(risky),
      tone: risky ? "warn" : undefined,
      note: danger > 0 ? t("landers.kpi.dangerLive", { n: formatInt(danger) }) : t("landers.kpi.dangerNone"),
      noteTone: danger > 0 ? "warn" : undefined,
    },
  ];
  return <KpiStrip tiles={tiles} loading={library.isLoading || report.isLoading || schema.isLoading} />;
}
